import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { useCurrency } from '../../context/CurrencyContext';
import { useInquiry } from '../../context/InquiryContext';
import { useCountries } from '../../hooks/useCountries';
import { productsApi } from '../../services/apiService';
import { setDeliverLocal } from '../../utils/mobileSettingsStorage';
import './MobileCartPage.css';

const DEFAULT_LOCATION = {
  countryCode: 'IN',
  countryName: 'India',
  postalCode: '',
  flag: 'https://flagcdn.com/w80/in.png',
};

const paymentMethods = ['VISA', 'MC', 'AMEX', 'PayPal', 'DISC', 'JCB', 'T/T'];

const protectionTiles = [
  {
    id: 'secure',
    label: 'Secure payments',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    ),
  },
  {
    id: 'delivery',
    label: 'Guaranteed delivery',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="3" width="15" height="13" rx="1" />
        <path d="M16 8h4l3 4v4h-7V8z" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
      </svg>
    ),
  },
  {
    id: 'money-back',
    label: 'Money-back guarantee',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2" />
        <path d="M1 10h22" />
        <path d="M8 15h3M8 12h5" />
      </svg>
    ),
  },
  {
    id: 'support',
    label: '24/7 support',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8a6 6 0 10-12 0v4a2 2 0 002 2h1v-4H6" />
        <path d="M18 14h-3v-4h3a2 2 0 012 2 2 2 0 01-2 2z" />
        <path d="M12 18v2" />
      </svg>
    ),
  },
  {
    id: 'privacy',
    label: 'Data privacy',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
    ),
  },
];

const protectionSections = [
  {
    id: 'secure-payments',
    title: 'Secure payments',
    linkLabel: 'Learn about secure payments',
    linkTo: '/terms',
    description: [
      'Choose your preferred local payment methods, currencies, bank transfers, or deferred payment plans to pay.',
      'Every transaction you make through Alibaba.com is protected by SSL encryption and PCI DSS data security protocols.',
    ],
    icon: protectionTiles[0].icon,
    showPaymentMethods: true,
  },
  {
    id: 'guaranteed-delivery',
    title: 'Guaranteed delivery',
    linkLabel: 'Learn about guaranteed shipping',
    linkTo: '/terms',
    description: [
      'Better planning and managing inventory knowing that your order will be dispatched or delivered by the guaranteed date.',
      'In the rare case there is a delay, receive a 10% compensation on the total order amount.',
    ],
    icon: protectionTiles[1].icon,
  },
  {
    id: 'money-back-protection',
    title: 'Money-back protection',
    linkLabel: 'Learn about refunds and returns',
    linkTo: '/terms',
    description: [
      "If your order doesn't ship, is missing, or the products are more defective, incorrect, or damaged.",
    ],
    icon: protectionTiles[2].icon,
  },
  {
    id: 'support',
    title: '24/7 support',
    linkLabel: 'Learn more',
    linkTo: '/my-support',
    description: [
      'Access our virtual help center 24/7, or connect with live agents for order issues, reports, or inquiries.',
    ],
    icon: protectionTiles[3].icon,
  },
  {
    id: 'data-privacy',
    title: 'Data privacy',
    linkLabel: 'Learn how we protect your data',
    linkTo: '/privacy',
    description: [
      'We never share your data with third parties without your consent. All personal information is handled in accordance with the Alibaba.com Privacy Policy.',
    ],
    icon: protectionTiles[4].icon,
  },
];

const readSavedCart = () => {
  try {
    const rawValue = window.localStorage.getItem('mobile-cart-items');
    if (!rawValue) return [];
    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const readSavedLocation = () => {
  try {
    const rawValue = window.localStorage.getItem('mobile-cart-location');
    if (!rawValue) return DEFAULT_LOCATION;
    return { ...DEFAULT_LOCATION, ...JSON.parse(rawValue) };
  } catch {
    return DEFAULT_LOCATION;
  }
};

const resolveProducts = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.products)) return response.products;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getProductImage = (product) => {
  const firstImage = Array.isArray(product?.images) ? product.images[0] : product?.images;
  const imagePath = firstImage || product?.image || product?.imageUrl || product?.thumbnail;

  if (!imagePath) return null;
  if (typeof imagePath !== 'string') return null;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/uploads')) return imagePath;

  return `${API_BASE_URL}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
};

const getProductPrice = (product) => {
  if (product?.price !== undefined && product?.price !== null && product.price !== '') {
    return Number(product.price);
  }
  if (product?.priceMin !== undefined && product?.priceMax !== undefined && product.priceMin !== product.priceMax) {
    return `${Number(product.priceMin).toFixed(2)}-${Number(product.priceMax).toFixed(2)}`;
  }
  if (product?.priceMin !== undefined && product?.priceMin !== null && product.priceMin !== '') {
    return Number(product.priceMin);
  }
  return null;
};

const getProductMoq = (product) => product?.moq || product?.minOrderQty || product?.minimumOrderQuantity || 1;

const getProductCountry = (product) => (
  product?.country ||
  product?.seller?.country ||
  product?.supplier?.country ||
  product?.originCountry ||
  'CN'
);

const getSellerYears = (product) => (
  product?.seller?.memberYears ||
  product?.seller?.yearsInBusiness ||
  product?.memberYears ||
  product?.yearsInBusiness ||
  null
);

const normalizeCartItems = (items) => items.map((item) => ({
  ...item,
  quantity: Number(item.quantity || item.qty || 1),
  selected: item.selected !== false,
  price: Number(item.price || item.priceMin || 0),
}));

/** Map inquiry context line to cart row shape used by this page */
function inquiryItemToCartRow(inv) {
  const qty = Number(inv.quantity || 1);
  const min = Number(inv.priceMin) || 0;
  const max = Number(inv.priceMax) || min;
  return {
    ...inv,
    id: inv.id,
    name: inv.description || inv.name,
    description: inv.description,
    image: inv.image,
    priceMin: min,
    priceMax: max,
    price: min,
    quantity: qty,
    moq: inv.moq,
    seller: inv.seller || { country: inv.country },
    sellerId: inv.sellerId,
  };
}

const MobileCartPage = () => {
  const navigate = useNavigate();
  const { formatPrice, formatPriceRange, selectedCurrency } = useCurrency();
  const { inquiryItems, addToInquiry, updateQuantity, removeFromInquiry } = useInquiry();
  const { countries, loading: countriesLoading } = useCountries();
  const legacyMergedRef = useRef(false);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [isProtectionOpen, setIsProtectionOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isCountryListOpen, setIsCountryListOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState(() => readSavedLocation());
  const [draftLocation, setDraftLocation] = useState(() => readSavedLocation());
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [checkoutError, setCheckoutError] = useState('');
  const sheetRef = useRef(null);

  const cartRows = useMemo(() => inquiryItems.map(inquiryItemToCartRow), [inquiryItems]);

  useEffect(() => {
    if (legacyMergedRef.current) return;
    legacyMergedRef.current = true;
    const legacy = normalizeCartItems(readSavedCart());
    if (!legacy.length || inquiryItems.length > 0) return;
    legacy.forEach((item) => {
      const sid = item.sellerId ?? item.seller?.userId ?? item.userId;
      if (sid == null || sid === '') return;
      const n = parseInt(String(sid), 10);
      addToInquiry({
        id: item.id,
        image: getProductImage(item) || item.image,
        description: item.name || item.description || 'Product',
        priceMin: item.priceMin ?? item.price,
        priceMax: item.priceMax ?? item.priceMin ?? item.price,
        priceUnit: selectedCurrency?.code || 'USD',
        pricePer: item.unit || item.pricePer || 'Unit',
        moq: item.moq || '1 Unit',
        companyName: item.companyName || item.sellerName || 'Supplier',
        sellerId: Number.isFinite(n) ? n : sid,
        sellerName: item.sellerName || item.companyName,
        sellerAvatar: item.sellerAvatar,
        country: item.country || 'UAE',
        quantity: item.quantity || 1,
      });
    });
    try {
      window.localStorage.removeItem('mobile-cart-items');
    } catch {
      /* ignore */
    }
  }, [addToInquiry, inquiryItems.length, selectedCurrency?.code]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await productsApi.getAll({ limit: 12 });
        const products = resolveProducts(response)
          .filter((product) => product?.status !== 'draft')
          .slice(0, 12);
        setRecommendedProducts(products);
      } catch {
        setRecommendedProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, []);

  useEffect(() => {
    window.localStorage.setItem('mobile-cart-location', JSON.stringify(deliveryLocation));
  }, [deliveryLocation]);

  useEffect(() => {
    if (!countries.length) return;

    const matchedCountry = countries.find((country) => country.code?.toUpperCase() === deliveryLocation.countryCode?.toUpperCase())
      || countries.find((country) => country.name?.toLowerCase() === deliveryLocation.countryName?.toLowerCase())
      || countries.find((country) => country.code?.toUpperCase() === 'IN');

    if (!matchedCountry) return;

    setDeliveryLocation((currentLocation) => ({
      ...currentLocation,
      countryCode: matchedCountry.code?.toUpperCase() || currentLocation.countryCode,
      countryName: matchedCountry.name || currentLocation.countryName,
      flag: matchedCountry.flag || currentLocation.flag,
    }));
  }, [countries]);

  const selectableItems = cartRows;

  useEffect(() => {
    setSelectedItemIds(selectableItems.map((item) => String(item.id)));
  }, [selectableItems]);

  useEffect(() => {
    if (!isProtectionOpen && !isLocationOpen) {
      document.body.classList.remove('mc-sheet-open');
      document.body.style.overflow = '';
      return undefined;
    }

    document.body.classList.add('mc-sheet-open');
    document.body.style.overflow = 'hidden';
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsProtectionOpen(false);
        setIsLocationOpen(false);
        setIsCountryListOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.classList.remove('mc-sheet-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isProtectionOpen, isLocationOpen]);

  const selectedItems = selectableItems.filter((item) => selectedItemIds.includes(String(item.id)));
  const allSelected = selectableItems.length > 0 && selectedItems.length === selectableItems.length;
  const totalPrice = selectedItems.reduce((sum, item) => {
    const numericPrice = Number(item.price || item.priceMin || 0);
    const quantity = Number(item.quantity || item.qty || 1);
    return sum + (Number.isNaN(numericPrice) ? 0 : numericPrice * quantity);
  }, 0);

  const handleSelectAll = () => {
    setCheckoutError('');
    if (allSelected) {
      setSelectedItemIds([]);
      return;
    }

    setSelectedItemIds(selectableItems.map((item) => String(item.id)));
  };

  const handleCloseSheet = () => {
    setIsProtectionOpen(false);
  };

  const handleOpenLocationSheet = () => {
    setDraftLocation(deliveryLocation);
    setCountrySearch('');
    setIsCountryListOpen(false);
    setIsLocationOpen(true);
  };

  const handleCloseLocationSheet = () => {
    setIsLocationOpen(false);
    setIsCountryListOpen(false);
    setCountrySearch('');
  };

  const handleSelectCountry = (country) => {
    setDraftLocation((currentLocation) => ({
      ...currentLocation,
      countryCode: country.code?.toUpperCase() || currentLocation.countryCode,
      countryName: country.name || currentLocation.countryName,
      flag: country.flag || currentLocation.flag,
    }));
    setIsCountryListOpen(false);
    setCountrySearch('');
  };

  const handleConfirmLocation = () => {
    const next = {
      ...draftLocation,
      postalCode: draftLocation.postalCode?.trim() || '',
    };
    setDeliveryLocation(next);
    setDeliverLocal({
      countryCode: next.countryCode,
      countryName: next.countryName,
      postalCode: next.postalCode || '',
    });
    handleCloseLocationSheet();
  };

  const handleToggleSelection = (itemId) => {
    const normalizedId = String(itemId);
    setCheckoutError('');
    setSelectedItemIds((currentIds) => (
      currentIds.includes(normalizedId)
        ? currentIds.filter((id) => id !== normalizedId)
        : [...currentIds, normalizedId]
    ));
  };

  const handleCheckout = () => {
    if (selectedItems.length === 0) {
      setCheckoutError('Select at least one product from your inquiry cart to check out.');
      return;
    }

    setCheckoutError('');
    navigate('/send-inquire');
  };

  const filteredCountries = countries.filter((country) => {
    const searchValue = countrySearch.trim().toLowerCase();
    if (!searchValue) return true;
    return country.name?.toLowerCase().includes(searchValue) || country.code?.toLowerCase().includes(searchValue);
  });

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string' && value.includes('-')) {
      const [minPrice, maxPrice] = value.split('-');
      return formatPriceRange(minPrice, maxPrice);
    }
    return formatPrice(value);
  };

  return (
    <div className="mc-page">
      <div className="mc-mobile-shell">
        <header className="mc-header">
          <div className="mc-header-main">
            <h1 className="mc-title">Cart({cartRows.length})</h1>
            <button type="button" className="mc-location" onClick={handleOpenLocationSheet}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>To {deliveryLocation.countryCode}</span>
            </button>
          </div>
          <button type="button" className="mc-wishlist" aria-label="Wishlist" onClick={() => navigate('/mobile/favorites')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
            </svg>
          </button>
        </header>

        <main className="mc-scroll">
          {cartRows.length === 0 && (
            <section className="mc-empty-card">
              <div className="mc-empty-illustration" aria-hidden="true">
                <svg viewBox="0 0 180 120" fill="none">
                  <path d="M55 37h63l-8 34H68L55 37z" fill="#f7c37a" />
                  <path d="M60 44h53l-3 12H63l-3-12z" fill="#ffd8a4" />
                  <path d="M111 33l17 12" stroke="#5f6f85" strokeWidth="3" strokeLinecap="round" />
                  <path d="M125 20l3 25" stroke="#5f6f85" strokeWidth="3" strokeLinecap="round" />
                  <path d="M55 37l-9 8" stroke="#b37e4f" strokeWidth="4" strokeLinecap="round" />
                  <path d="M49 70l18 11" stroke="#69768b" strokeWidth="8" strokeLinecap="round" />
                  <path d="M110 71l13 11" stroke="#69768b" strokeWidth="8" strokeLinecap="round" />
                  <circle cx="73" cy="87" r="8" fill="#586271" />
                  <circle cx="114" cy="87" r="8" fill="#586271" />
                  <circle cx="73" cy="87" r="3" fill="#9aa4b1" />
                  <circle cx="114" cy="87" r="3" fill="#9aa4b1" />
                </svg>
              </div>
              <p className="mc-empty-title">Your cart is empty</p>
              <button type="button" className="mc-source-button" onClick={() => navigate('/all-categories')}>
                Source by category
              </button>
            </section>
          )}

          {cartRows.length > 0 && (
            <section className="mc-cart-lines" aria-label="Inquiry cart items">
              {cartRows.map((row) => (
                <article key={row.id} className="mc-cart-line">
                  <label className="mc-product-check mc-cart-line-check" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedItemIds.includes(String(row.id))}
                      onChange={() => handleToggleSelection(row.id)}
                    />
                    <span className="mc-product-check-box" />
                  </label>
                  <button type="button" className="mc-cart-line-thumb" onClick={() => navigate(`/mobile/product/${row.id}`)}>
                    {row.image ? (
                      <img src={typeof row.image === 'string' ? row.image : getProductImage(row)} alt="" />
                    ) : (
                      <div className="mc-product-image-placeholder" />
                    )}
                  </button>
                  <div className="mc-cart-line-body">
                    <button type="button" className="mc-cart-line-title" onClick={() => navigate(`/mobile/product/${row.id}`)}>
                      {row.name || 'Product'}
                    </button>
                    <p className="mc-cart-line-price">{formatCurrency(getProductPrice(row))}</p>
                    <div className="mc-cart-line-qty">
                      <button
                        type="button"
                        className="mc-qty-btn"
                        aria-label="Decrease quantity"
                        onClick={() => updateQuantity(row.id, Math.max(1, (row.quantity || 1) - 1))}
                      >
                        −
                      </button>
                      <span className="mc-qty-val">{row.quantity || 1}</span>
                      <button
                        type="button"
                        className="mc-qty-btn"
                        aria-label="Increase quantity"
                        onClick={() => updateQuantity(row.id, (row.quantity || 1) + 1)}
                      >
                        +
                      </button>
                      <button type="button" className="mc-cart-line-remove" onClick={() => removeFromInquiry(row.id)}>
                        Remove
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </section>
          )}

          <button type="button" className="mc-protection-card" onClick={() => setIsProtectionOpen(true)}>
            <div className="mc-protection-copy">
              <div className="mc-protection-heading-row">
                <h2 className="mc-protection-title">Alibaba.com order protection</h2>
                <svg className="mc-protection-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
              <p className="mc-protection-subtitle">Only orders placed and paid through Alibaba.com can enjoy free protection by Trade Assurance</p>
              <div className="mc-trade-assurance">
                <span className="mc-trade-assurance-icon">$</span>
                <span>Trade Assurance</span>
              </div>
            </div>

            <div className="mc-tile-grid">
              {protectionTiles.map((tile) => (
                <div key={tile.id} className="mc-tile-item">
                  <div className="mc-tile-icon">{tile.icon}</div>
                  <span>{tile.label}</span>
                </div>
              ))}
            </div>

            <div className="mc-payment-row">
              <span className="mc-payment-label">Payments supported:</span>
              <div className="mc-payment-list">
                {paymentMethods.map((method) => (
                  <span key={method} className="mc-payment-chip">{method}</span>
                ))}
              </div>
            </div>
          </button>

          <section className="mc-recommended-section">
            <h2 className="mc-recommended-title">Recommended for you</h2>
            <div className="mc-product-grid">
              {loadingProducts && [1, 2, 3, 4].map((item) => <div key={item} className="mc-product-skeleton" />)}
              {!loadingProducts && recommendedProducts.length === 0 && (
                <p className="mc-recommended-empty">No products available</p>
              )}
              {!loadingProducts && recommendedProducts.map((product) => {
                const image = getProductImage(product);
                const price = formatCurrency(getProductPrice(product));
                const years = getSellerYears(product);

                return (
                  <article
                    key={product.id}
                    className="mc-product-card"
                    onClick={() => navigate(`/mobile/product/${product.id}`)}
                  >
                    <div className="mc-product-image-wrap">
                      {image ? (
                        <img src={image} alt={product.name || product.title || 'Product'} className="mc-product-image" />
                      ) : (
                        <div className="mc-product-image-placeholder">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        </div>
                      )}
                      <button type="button" className="mc-scan-badge" aria-label="Scan product image">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 9V5a2 2 0 012-2h4M15 3h4a2 2 0 012 2v4M21 15v4a2 2 0 01-2 2h-4M9 21H5a2 2 0 01-2-2v-4" />
                        </svg>
                      </button>
                    </div>

                    <div className="mc-product-content">
                      <p className="mc-product-name">{product.name || product.title || 'Product'}</p>
                      {price && <p className="mc-product-price">{price}</p>}
                      <p className="mc-product-moq">MOQ: {getProductMoq(product)}</p>
                      <p className="mc-product-country">{years ? `${years} yr${Number(years) > 1 ? 's' : ''} · ` : ''}{getProductCountry(product)}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </main>

        <div className="mc-bottom-bar">
          <label className="mc-select-all">
            <input type="checkbox" checked={allSelected} onChange={handleSelectAll} />
            <span>All</span>
          </label>

          <button type="button" className="mc-total-button">
            <span className="mc-total-value">{formatCurrency(totalPrice) || `${selectedCurrency.symbol}0`}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <button
            type="button"
            className={`mc-checkout-button ${selectedItems.length > 0 ? 'is-active' : ''}`}
            onClick={handleCheckout}
          >
            Check out
          </button>
        </div>

        {checkoutError && <div className="mc-checkout-error">{checkoutError}</div>}

        <nav className="mc-mobile-nav">
          <button type="button" className="mc-mobile-nav-item" onClick={() => navigate('/')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span>Home</span>
          </button>
          <button type="button" className="mc-mobile-nav-item" onClick={() => navigate('/all-categories')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
            </svg>
            <span>Categories</span>
          </button>
          <button type="button" className="mc-mobile-nav-item" onClick={() => navigate('/mobile/messenger')}>
            <span className="mc-mobile-nav-badge">8</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>Messenger</span>
          </button>
          <button type="button" className="mc-mobile-nav-item is-active">
            <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.6">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" fill="none" />
            </svg>
            <span>Cart</span>
          </button>
          <button type="button" className="mc-mobile-nav-item" onClick={() => navigate('/mobile/alibaba')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span>My Alibaba</span>
          </button>
        </nav>
      </div>

      {isProtectionOpen && (
        <div className="mc-sheet-overlay" onClick={handleCloseSheet}>
          <div className="mc-sheet" ref={sheetRef} onClick={(event) => event.stopPropagation()}>
            <div className="mc-sheet-handle" />
            <div className="mc-sheet-header">
              <h2 className="mc-sheet-title">Protections</h2>
              <button type="button" className="mc-sheet-close" onClick={handleCloseSheet} aria-label="Close protections sheet">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="mc-sheet-body">
              <h3 className="mc-sheet-brand-title">Alibaba.com order protection</h3>

              <section className="mc-sheet-info-box">
                <div className="mc-sheet-info-title-row">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 7v5" />
                    <path d="M12 16h.01" />
                  </svg>
                  <h4>How to keep your order protected</h4>
                </div>

                <div className="mc-sheet-info-item">
                  <span className="mc-sheet-info-bullet" />
                  <div>
                    <strong>When placing an order yourself:</strong>
                    <p>Your order is protected from the moment you select "Start order" on the product page.</p>
                  </div>
                </div>

                <div className="mc-sheet-info-item">
                  <span className="mc-sheet-info-bullet" />
                  <div>
                    <strong>When supplier drafts an order for you:</strong>
                    <p>Make payment on Alibaba.com by going to My Alibaba &gt; Orders. If paying by wire transfer, make sure you pay to the official Alibaba.com bank account.</p>
                  </div>
                </div>
              </section>

              {protectionSections.map((section) => (
                <section key={section.id} className="mc-sheet-section">
                  <div className="mc-sheet-section-icon">{section.icon}</div>
                  <div className="mc-sheet-section-content">
                    <h4>{section.title}</h4>
                    {section.description.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {section.showPaymentMethods && (
                      <div className="mc-sheet-payment-list">
                        {paymentMethods.map((method) => (
                          <span key={method} className="mc-sheet-payment-chip">{method}</span>
                        ))}
                      </div>
                    )}
                    <button type="button" className="mc-sheet-link" onClick={() => navigate(section.linkTo)}>
                      {section.linkLabel}
                    </button>
                  </div>
                </section>
              ))}

              <p className="mc-sheet-terms">Terms and conditions apply</p>
            </div>
          </div>
        </div>
      )}

      {isLocationOpen && (
        <div className="mc-sheet-overlay" onClick={handleCloseLocationSheet}>
          <div className="mc-location-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="mc-sheet-handle" />
            <div className="mc-location-header">
              <h2 className="mc-location-title">Deliver to</h2>
              <button type="button" className="mc-sheet-close" onClick={handleCloseLocationSheet} aria-label="Close deliver to sheet">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="mc-location-body">
              <p className="mc-location-copy">Confirm your location for more accurate shipping estimates and wider range of logistics services</p>

              <div className="mc-location-field-group">
                <label className="mc-location-label">Country/region</label>
                <button type="button" className="mc-country-trigger" onClick={() => setIsCountryListOpen((currentValue) => !currentValue)}>
                  <span className="mc-country-trigger-left">
                    <img src={draftLocation.flag} alt={draftLocation.countryName} className="mc-country-flag" />
                    <span>{draftLocation.countryName} ({draftLocation.countryCode})</span>
                  </span>
                  <svg className={`mc-country-chevron ${isCountryListOpen ? 'is-open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {isCountryListOpen && (
                  <div className="mc-country-dropdown">
                    <input
                      type="text"
                      className="mc-country-search"
                      placeholder="Search country"
                      value={countrySearch}
                      onChange={(event) => setCountrySearch(event.target.value)}
                    />
                    <div className="mc-country-options">
                      {countriesLoading && <p className="mc-country-empty">Loading countries…</p>}
                      {!countriesLoading && filteredCountries.map((country) => (
                        <button
                          type="button"
                          key={country.id || country.code}
                          className={`mc-country-option ${draftLocation.countryCode === country.code?.toUpperCase() ? 'is-selected' : ''}`}
                          onClick={() => handleSelectCountry(country)}
                        >
                          <span className="mc-country-option-main">
                            <img src={country.flag} alt={country.name} className="mc-country-flag" />
                            <span>{country.name} ({country.code?.toUpperCase()})</span>
                          </span>
                        </button>
                      ))}
                      {!countriesLoading && filteredCountries.length === 0 && <p className="mc-country-empty">No countries found</p>}
                    </div>
                  </div>
                )}
              </div>

              <div className="mc-location-field-group">
                <label className="mc-location-label">Postal code (optional)</label>
                <input
                  type="text"
                  value={draftLocation.postalCode}
                  onChange={(event) => setDraftLocation((currentLocation) => ({
                    ...currentLocation,
                    postalCode: event.target.value,
                  }))}
                  className="mc-postal-input"
                  placeholder="Postal code (optional)"
                />
              </div>
            </div>

            <div className="mc-location-footer">
              <button type="button" className="mc-location-confirm" onClick={handleConfirmLocation}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileCartPage;